import React from 'react';
import { IconSlot, type IconSlotProps } from '~/design-system/components/v1_5/Header';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './PanelHeader.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface PanelHeaderProps {
  /** The leading run — a title, a breadcrumb, whatever names this panel. Laid out on the band's 7px. */
  children?: React.ReactNode;
  /** The trailing cluster, 7px apart. `PanelHeaderButton`s, in practice. */
  trailing?: React.ReactNode;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The 36px band at the top of a panel: 35px of content over a 1px hairline.
 *
 * Deliberately generic. The agent pane puts one word in it and the browser pane puts a breadcrumb
 * and three controls, and the band knows about neither — it owns the height, the 12/6 padding, the
 * 7px rhythm and the hairline, and nothing else. Whatever goes in the leading run is pushed left by
 * a flexible middle that carries no content of its own.
 */
export function PanelHeader({ children, trailing, theme: themeProp, className }: PanelHeaderProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(themes[theme], styles.band, className)}>
      {/* The leading run gets its own box rather than sitting as direct children of the band, and
          the box is the one thing here allowed to shrink. As direct children they could not: the
          band is `nowrap`, so a long artifact name grew the run past the pane, shoved the trailing
          cluster out of the shell, and `.root { overflow: clip }` deleted it — including the fold
          toggle that D4 makes the ONLY way to bring a folded directory back. The wrapper carries
          the band's own 7px so the rhythm is unchanged. */}
      {children ? <div className={styles.leading}>{children}</div> : null}
      <div className={styles.middle} aria-hidden />
      {trailing ? <div className={styles.trailing}>{trailing}</div> : null}
    </div>
  );
}

/**
 * `IconSlot`'s own props, with `glyphSize` re-declared only to move its default from 18 to 15.
 * Derived rather than restated on purpose: this control has no API of its own, and a second
 * hand-written prop list is exactly how the two drift apart.
 */
export type PanelHeaderButtonProps = Omit<IconSlotProps, 'glyphSize'> & {
  /** 15 for the browser header's three; the directory's `+` is drawn at 16 for optical weight. */
  glyphSize?: number;
};

/**
 * The panel header's control shape: `IconSlot`, resized to the 24px these bands use.
 *
 * Two slot systems coexist in v1.5 — 30x30 in the 40px application header, 24x24 in these 36px
 * bands — and this is the smaller one. It is a size override on the shipped slot rather than a
 * second component, because everything a slot does apart from those two numbers is a reviewed
 * interaction recipe: the chip as a pseudo-element so only opacity animates, ACTIVE painting
 * nothing and being the `-fill` twin instead, the focus ring, the 0.97 press, the hover gate and
 * the reduced-motion carve-out. Directory and Preview both want this same 24px slot; a copy here
 * would be a copy in three places within the week.
 *
 * It lives beside `PanelHeader` rather than in its own file for the same reason `MenuSpacer` lives
 * beside `MenuSurface`: it is the trailing slot's only inhabitant and it shares the stylesheet.
 */
export const PanelHeaderButton = React.forwardRef<HTMLButtonElement, PanelHeaderButtonProps>(
  function PanelHeaderButton({ glyphSize = 15, className, ...rest }, ref) {
    // `.compact` is doubled in the stylesheet so it beats `IconSlot`'s own `.slot` on specificity
    // rather than on which module the bundler happened to emit first.
    return (
      <IconSlot
        ref={ref}
        glyphSize={glyphSize}
        {...rest}
        className={cn(styles.compact, className)}
      />
    );
  },
);
